import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tooltip } from "antd";
import { useAuth } from "../../contexts/AuthContext";
import { Role } from "../../constants/Role";

const styles = {
  actionBtn: {
    display: "flex",
    alignItems: "center",
    borderRadius: 7,
    fontWeight: 500,
    border: "none",
    padding: "2px 12px",
    fontSize: 14,
    height: 30,
    cursor: "pointer",
    transition: "background 0.2s, color 0.2s, border 0.2s",
    opacity: 0,
    pointerEvents: "none",
  },
  editBtn: {
    background: "#1677ff",
    color: "#fff",
    marginLeft: 18,
  },
  deleteBtn: {
    background: "transparent",
    color: "#f77070",
    border: "1.5px solid #f77070",
    marginLeft: 10,
  },
  icon: {
    marginRight: 4,
    display: "flex",
    alignItems: "center",
  },
  collapseBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    marginRight: 10,
    cursor: "pointer",
    color: "#888",
    outline: "none",
    display: "flex",
    alignItems: "center",
    height: 28,
  },
};

// ==== Sortable Section voi Collapse ====
export function SectionSortable({ section, children, onEdit, onDelete, expanded, onExpand, onCollapse }) {
  // Auth context
  const { hasAnyRole } = useAuth();
  
  // Check permissions
  const isAdminDev = hasAnyRole([Role.ADMIN_DEV]);
  const isStaffCourseManager = hasAnyRole([Role.STAFF_COURSE_MANAGER]);
  
  // Determine if user can perform CUD operations
  const canUpdate = isAdminDev || isStaffCourseManager;
  const canDelete = isAdminDev || isStaffCourseManager;
  
  const [internalCollapsed, setInternalCollapsed] = useState(true);
  const collapsed = typeof expanded === 'boolean' ? !expanded : internalCollapsed;
  const [hovered, setHovered] = useState(false);
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { type: "section" },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 8,
        margin: "10px 0",
        boxShadow: isDragging ? "0 4px 16px #dfdfff" : "none",
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "14px 16px",
          cursor: isDragging ? "grabbing" : "grab",
          borderBottom: "1px solid #f5f5f5",
          background: isDragging ? "#f2e8ff" : "#f7f7f7",
          position: "relative",
        }}
      >
        {/* Collapse button */}
        <button
          type="button"
          style={styles.collapseBtn}
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            if (typeof expanded === 'boolean') {
              if (collapsed && onExpand) onExpand();
              if (!collapsed && onCollapse) onCollapse();
            } else {
              setInternalCollapsed((prev) => !prev);
            }
          }}
          aria-label={collapsed ? "Expand section" : "Collapse section"}
        >
          {collapsed ? (
            // Chevron right
            <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
              <path
                d="M6 12l4-4-4-4"
                stroke="#888"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            // Chevron down
            <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
              <path
                d="M4 6l4 4 4-4"
                stroke="#888"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
        {/* Drag handle */}
        <span
          {...listeners}
          style={{ marginRight: 10, fontSize: 20, userSelect: "none", opacity: isDragging ? 0.6 : 1 }}
        >
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
            <path d="M2.5 12.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0-4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0-4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11z" />
          </svg>
        </span>
        {/* Section title */}
        <Tooltip title={section.title} placement="topLeft">
          <span
            style={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              verticalAlign: "middle",
              width: 170,
              marginRight: 8,
            }}
          >
            {section.title}
          </span>
        </Tooltip>
        <span style={{ marginLeft: "auto", color: "#888", fontSize: 14 }}>{section.lessons?.length || 0} lessons</span>
        {canUpdate && (
          <button
            type="button"
            style={{
              ...styles.actionBtn,
              ...styles.editBtn,
              opacity: hovered ? 1 : 0,
              pointerEvents: hovered ? "auto" : "none",
            }}
            onClick={() => onEdit && onEdit(section)}
            title="Edit section"
          >
            <i className="bi bi-pencil-square" style={styles.icon}></i>
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            style={{
              ...styles.actionBtn,
              ...styles.deleteBtn,
              opacity: hovered ? 1 : 0,
              pointerEvents: hovered ? "auto" : "none",
            }}
            onClick={() => onDelete && onDelete(section)}
            title="Delete section"
          >
            <i className="bi bi-trash" style={styles.icon}></i>
          </button>
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}

export function LessonSortable({
  lesson,
  sectionId,
  onEdit,
  onDelete,
  isActive, // Truyền từ cha vào: true nếu là lesson đang được chọn
}) {
  // Auth context
  const { hasAnyRole } = useAuth();
  
  // Check permissions
  const isAdminDev = hasAnyRole([Role.ADMIN_DEV]);
  const isStaffCourseManager = hasAnyRole([Role.STAFF_COURSE_MANAGER]);
  
  // Determine if user can perform CUD operations
  const canUpdate = isAdminDev || isStaffCourseManager;
  const canDelete = isAdminDev || isStaffCourseManager;
  
  const [hovered, setHovered] = useState(false);
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: lesson.id,
    data: { type: "lesson", sectionId },
  });

  // Chọn màu chính
  const PRIMARY_COLOR = "#1677ff";
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      onClick={() => lesson.onSelect && lesson.onSelect()}
      style={{
        background: isActive ? "#eaf1fd" : "#f8f8ff",
        border: isActive ? `1.5px solid ${PRIMARY_COLOR}` : "1px solid #eee",
        borderRadius: 6,
        margin: "6px 0",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        cursor: isDragging ? "grabbing" : "pointer",
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition,
        fontWeight: 500,
        boxShadow: isDragging ? "0 2px 8px #eee" : undefined,
        position: "relative",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Drag handle */}
      <span
        {...listeners}
        style={{
          marginRight: 10,
          opacity: isDragging ? 0.5 : 1,
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5 12.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0-4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0-4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11z" />
        </svg>
      </span>
      {/* Nút tròn select */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          lesson.onSelect && lesson.onSelect();
        }}
        style={{
          width: 20,
          height: 20,
          minWidth: 20,
          minHeight: 20,
          marginRight: 12,
          border: `2px solid ${isActive ? PRIMARY_COLOR : "#bbb"}`,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          cursor: "pointer",
          boxSizing: "border-box",
        }}
      >
        {isActive && (
          <span
            style={{
              display: "block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: PRIMARY_COLOR,
            }}
          />
        )}
      </span>
      {/* Title + icon */}
      <Tooltip title={lesson.title} placement="topLeft">
        <div style={{ display: "flex", flexDirection: "row" }}>
          <span
            style={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              verticalAlign: "middle",
              width: 170,
              marginRight: 8,
            }}
            onClick={(e) => {
              e.stopPropagation();
              lesson.onSelect && lesson.onSelect();
            }}
          >
            {lesson.title}
          </span>
          <span>
            {lesson.type === "Video" ? (
              <i
                className="bi bi-camera-reels"
                style={{ color: PRIMARY_COLOR, fontSize: 18, verticalAlign: "middle" }}
              ></i>
            ) : (
              <i
                className="bi bi-file-earmark-richtext"
                style={{ color: PRIMARY_COLOR, fontSize: 18, verticalAlign: "middle" }}
              ></i>
            )}
          </span>
        </div>
      </Tooltip>

      {canUpdate && (
        <button
          type="button"
          style={{
            ...styles.actionBtn,
            ...styles.editBtn,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? "auto" : "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit && onEdit(lesson);
          }}
          title="Edit"
        >
          <i className="bi bi-pencil-square" style={styles.icon}></i>
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          style={{
            ...styles.actionBtn,
            ...styles.deleteBtn,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? "auto" : "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete && onDelete(lesson);
          }}
          title="Delete"
        >
          <i className="bi bi-trash" style={styles.icon}></i>
        </button>
      )}
    </div>
  );
}
